import React from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import {
  HeartIcon,
  ChatBubbleLeftIcon,
  UserPlusIcon,
  CameraIcon,
  BellIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import Avatar from '../common/Avatar';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';

interface Activity {
  id: string;
  type: 'follow' | 'message' | 'collaboration' | 'opportunity' | 'like' | 'application';
  title: string;
  description: string;
  user?: {
    name: string;
    avatar: string;
    id: string;
  };
  timestamp: string;
  link?: string;
}

interface ActivityFeedProps {
  activities: Activity[];
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities }) => {
  const navigate = useNavigate();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'follow':
        return UserPlusIcon;
      case 'message':
        return ChatBubbleLeftIcon;
      case 'collaboration':
        return CameraIcon;
      case 'opportunity':
        return BellIcon;
      case 'like':
        return HeartIcon;
      case 'application':
        return CheckCircleIcon;
      default:
        return BellIcon;
    }
  };

  return (
    <Card className="border-none shadow-sm h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base md:text-lg font-bold">Recent Activity</CardTitle>
        <Link 
          to="/notifications"
          className="text-sm text-gray-500 hover:text-black font-medium transition-colors dark:text-gray-400 dark:hover:text-white"
        >
          View All →
        </Link>
      </CardHeader>

      <CardContent>
        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
          {activities.map((activity, index) => {
            const Icon = getActivityIcon(activity.type);
            
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => activity.link && navigate(activity.link)}
                className={`flex items-start space-x-4 p-3 md:p-4 rounded-2xl hover:bg-gray-50 transition-all cursor-pointer border border-transparent hover:border-gray-100 dark:hover:bg-gray-800 dark:hover:border-gray-700`}
              >
                {/* Activity Icon */}
                <div className="p-3 rounded-xl bg-gray-100 text-black flex-shrink-0 dark:bg-gray-700 dark:text-white">
                  <Icon className="h-5 w-5" />
                </div>

                {/* Activity Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {activity.user && (
                      <Avatar
                        src={activity.user.avatar}
                        alt={activity.user.name}
                        size="sm"
                        className="w-6 h-6 ring-2 ring-white dark:ring-gray-800"
                      />
                    )}
                    <p className="text-xs md:text-sm font-semibold text-gray-900 truncate dark:text-white">
                      {activity.title}
                    </p>
                  </div>
                  
                  <p className="text-[10px] md:text-sm text-gray-600 mb-2 leading-relaxed dark:text-gray-300">
                    {activity.description}
                  </p>
                  
                  <p className="text-[10px] md:text-xs text-gray-400 font-medium dark:text-gray-500">
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {activities.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <div className="bg-white p-4 rounded-full inline-block mb-4 shadow-sm dark:bg-gray-700">
              <BellIcon className="h-8 w-8 text-gray-400 dark:text-gray-300" />
            </div>
            <p className="text-gray-900 font-medium mb-1 dark:text-white">No recent activity</p>
            <p className="text-gray-500 text-sm dark:text-gray-400">
              Start connecting with creators and models
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
